import { Component, Input, ViewEncapsulation, OnInit, OnDestroy, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { Router, NavigationEnd, NavigationStart, NavigationCancel, NavigationError } from '@angular/router';
import { NavService, Menu } from '../../service/nav.service';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Subject, interval } from 'rxjs';
import { takeUntil, filter } from 'rxjs/operators';
import { APIService } from '../../service/apiService.service';

@Component({
  selector: 'app-sidebar',
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SidebarComponent implements OnInit, OnDestroy {

  public menuItems: Menu[] = [];
  public url: any;
  public fileurl: any;
  public profile: any;
  public dashboardLink: string = '/dashboard'; // Enlace dinámico para el logo
  public isNavigating: boolean = false; // Estado para spinner en el sidebar
  private destroy$ = new Subject<void>();

  constructor(
  private router: Router, 
    public navServices: NavService, 
  private modalService: NgbModal,
  private cdr: ChangeDetectorRef,
  private apiService: APIService
  ) {
  // console.time('sidebar-constructor'); // SILENCIADO
  this.loadData();
  // console.timeEnd('sidebar-constructor'); // SILENCIADO
  }

  ngOnInit() {
  // console.time('sidebar-init'); // SILENCIADO
    // Primero cargar datos de localStorage (rápido)
    this.loadData();
    
    // Luego cargar perfil actualizado del API
    this.loadProfile();
    
    this.setupNavigation();
    
    // Escuchar cambios en localStorage (cuando otro componente actualiza el perfil)
    window.addEventListener('storage', (event) => {
      if (event.key === 'currentUser' && event.newValue) {
        this.loadData();
        this.cdr.markForCheck();
      }
    });
    
    // Verificar cambios en localStorage cada 5 segundos para actualizar la foto de perfil
    interval(5000)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        const previousImg = this.profile?.imgProfile;
        this.loadData();
        // Solo detectar cambios si la imagen cambió
        if (previousImg !== this.profile?.imgProfile) {
          this.cdr.markForCheck();
        }
      });
  // console.timeEnd('sidebar-init'); // SILENCIADO
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private setupNavigation() {
    // Optimización: usar takeUntil para evitar memory leaks
    this.navServices.items
      .pipe(takeUntil(this.destroy$))
      .subscribe(menuItems => {
        // Optimización: solo filtrar si hay profile y roles
        if (this.profile?.roles) {
          // console.time('menu-filter'); // SILENCIADO
          this.menuItems = this.filterMenuItems(menuItems);
          // console.timeEnd('menu-filter'); // SILENCIADO
        } else {
          this.menuItems = [];
        }
      });

    // Optimización: filtrar solo eventos de NavigationEnd
    this.router.events
      .pipe(takeUntil(this.destroy$))
      .subscribe((event) => {
        // Estado de navegación para mostrar spinners en items activos
        if (event instanceof NavigationStart) {
          this.isNavigating = true;
          this.cdr.markForCheck();
        }
        if (event instanceof NavigationEnd || event instanceof NavigationCancel || event instanceof NavigationError) {
          this.isNavigating = false;
          this.cdr.markForCheck();
        }

        // Actualizar activos solo al finalizar navegación
        if (event instanceof NavigationEnd) {
          // console.time('navigation-update'); // SILENCIADO
          this.updateActiveStates(event.url);
          // console.timeEnd('navigation-update'); // SILENCIADO
        }
      });
  }

  private filterMenuItems(menuItems: Menu[]): Menu[] {
    return menuItems.filter((item: Menu) => {
      if (!item.rol) return true;
      return item.rol.includes(this.profile.roles);
    });
  }

  private updateActiveStates(currentUrl: string) {
    this.menuItems.forEach(menuItem => {
      this.processMenuItem(menuItem, currentUrl);
    });
  }

  private processMenuItem(item: Menu, currentUrl: string) {
    if (item.path === currentUrl) {
      this.setNavActive(item);
      return;
    }

    if (item.children) {
      item.children.forEach(subItem => {
        this.processMenuItem(subItem, currentUrl);
        if (subItem.children) {
          subItem.children.forEach(subSubItem => {
            this.processMenuItem(subSubItem, currentUrl);
          });
        }
      });
    }
  }

  openAddCourseModal(content: any) {
    this.modalService.open(content, { ariaLabelledBy: 'modal-basic-title' });
  }

  // Cargar perfil desde el API
  loadProfile() {
    this.apiService.getProfile().subscribe({
      next: (response: any) => {
        if (response && response.success) {
          const userData = response.data;
          
          // Construir displayName con validación estricta
          const firstName = userData.firstName?.trim();
          const lastName = userData.lastName?.trim();
          
          if (firstName && lastName) {
            userData.displayName = `${firstName} ${lastName}`;
          } else if (firstName) {
            userData.displayName = firstName;
          } else if (lastName) {
            userData.displayName = lastName;
          } else if (userData.email) {
            userData.displayName = userData.email.split('@')[0];
          } else {
            userData.displayName = 'Usuario';
          }
          
                    // Construir imagen con prioridad: base64 > profileFileUrl > imgProfile
          let imageUrl = '';
          if (userData.profileImage?.base64) {
            imageUrl = `data:image/jpeg;base64,${userData.profileImage.base64}`;
          } else if (userData.profileFileUrl && this.isValidImageUrl(userData.profileFileUrl)) {
            imageUrl = userData.profileFileUrl;
          } else {
            imageUrl = 'assets/images/user.png';
          }
          
          // Actualizar perfil local EN MEMORIA (con imagen base64)
          this.profile = {
            ...userData,
            imgProfile: imageUrl
          };
          
          // Guardar en localStorage PRESERVANDO datos existentes como token
          try {
            const existingData = JSON.parse(localStorage.getItem('currentUser') || '{}');
            
            const userDataForStorage = {
              ...existingData, // Preservar token y otros datos existentes
              ...userData, // Actualizar con nuevos datos del perfil
              // NO incluir el base64 completo en profileImage
              profileImage: userData.profileImage ? {
                hasBase64: !!userData.profileImage.base64
              } : null,
              // Remover imgProfile si es base64
              imgProfile: imageUrl.startsWith('data:') ? undefined : imageUrl
            };
            
            localStorage.setItem('currentUser', JSON.stringify(userDataForStorage));
            
            // Guardar la imagen base64 en sessionStorage (separado)
            if (imageUrl?.startsWith('data:')) {
              try {
                sessionStorage.setItem('profileImageBase64', imageUrl);
              } catch (e) {
                console.warn('⚠️ [SIDEBAR] No se pudo guardar imagen en sessionStorage:', e);
              }
            }
            
            // Disparar evento de storage para notificar a otros componentes
            window.dispatchEvent(new StorageEvent('storage', {
              key: 'currentUser',
              newValue: JSON.stringify(userDataForStorage),
              url: window.location.href
            }));
          } catch (e) {
            console.error('❌ [SIDEBAR] Error al actualizar localStorage:', e);
          }
          
          this.cdr.markForCheck();
        }
      },
      error: (error) => {
        console.error('❌ [SIDEBAR] Error al cargar perfil:', error);
        // En caso de error, mantener los datos de localStorage
        this.loadData();
      }
    });
  }

  loadData() {
  // console.time('sidebar-loadData'); // SILENCIADO
    
    // Optimización: uso de try-catch más eficiente
    try {
      const userData = localStorage.getItem('currentUser');
      this.profile = userData ? JSON.parse(userData) : { roles: null, displayName: 'Usuario' };
      
      // Asegurar que displayName tenga un valor - con validación estricta
      if (!this.profile.displayName) {
        // Validar explícitamente que firstName y lastName no sean undefined/null/vacíos
        const firstName = this.profile.firstName?.trim();
        const lastName = this.profile.lastName?.trim();
        
        if (firstName && lastName) {
          this.profile.displayName = `${firstName} ${lastName}`;
        } else if (firstName) {
          this.profile.displayName = firstName;
        } else if (lastName) {
          this.profile.displayName = lastName;
        } else if (this.profile.email) {
          this.profile.displayName = this.profile.email.split('@')[0];
        } else {
          this.profile.displayName = 'Usuario';
        }
      }
      
      // Asegurarnos de que imgProfile tenga un valor válido
      // Primero intentar con sessionStorage (donde el header guarda la imagen base64)
      const cachedBase64 = sessionStorage.getItem('profileImageBase64');
      if (cachedBase64 && cachedBase64.startsWith('data:')) {
        this.profile.imgProfile = cachedBase64;
      }
      // Si no hay en sessionStorage, verificar otras opciones
      else if (!this.profile?.imgProfile || !this.isValidImageUrl(this.profile.imgProfile)) {
        // Intentar con profileFileUrl
        if (this.profile?.profileFileUrl && this.isValidImageUrl(this.profile.profileFileUrl)) {
          this.profile.imgProfile = this.profile.profileFileUrl;
        }
        // Si hay indicador de base64 pero no está cargado, usar imagen por defecto
        // (el loadProfile() del API la cargará después)
        else if (this.profile?.profileImage?.hasBase64) {
          this.profile.imgProfile = 'assets/images/user.png'; // Temporal hasta que cargue del API
        }
        // Si no hay nada, usar imagen por defecto
        else {
          this.profile.imgProfile = 'assets/images/user.png';
        }
      }
      
    } catch (error) {
      console.error('❌ [SIDEBAR] Error parsing user data from localStorage:', error);
      this.profile = { roles: null, displayName: 'Usuario' };
    }

    // Optimización: configuración de enlaces más directa
    this.dashboardLink = (this.profile?.roles === 'teacher' || this.profile?.roles === 'collaborator') 
      ? '/inicio' 
      : this.profile?.roles === 'student' 
        ? '/inicio'  // Los estudiantes también van a inicio
        : '/dashboard';
    
  // console.timeEnd('sidebar-loadData'); // SILENCIADO
  }

  // Obtener el nombre del rol para mostrar
  getRoleDisplayName(): string {
    if (!this.profile?.roles) return 'Usuario';
    
    const roleMap: { [key: string]: string } = {
      'teacher': 'Instructor',
      'collaborator': 'Colaborador',
      'student': 'Estudiante',
      'admin': 'Administrador'
    };
    
    return roleMap[this.profile.roles] || 'Usuario';
  }

  // Obtener el nombre del usuario para mostrar
  getDisplayName(): string {
    // Si ya existe displayName y es válido, usarlo
    if (this.profile?.displayName && this.profile.displayName !== 'undefined undefined') {
      return this.profile.displayName;
    }
    
    // Construir desde firstName y lastName
    const firstName = this.profile?.firstName?.trim();
    const lastName = this.profile?.lastName?.trim();
    
    if (firstName && lastName) {
      return `${firstName} ${lastName}`;
    } else if (firstName) {
      return firstName;
    } else if (lastName) {
      return lastName;
    } else if (this.profile?.email) {
      return this.profile.email.split('@')[0];
    }
    
    return 'Usuario';
  }

  // Optimización: Active Nave state más eficiente
  setNavActive(item: Menu) {
  // console.time('setNavActive'); // SILENCIADO
    
    // Desactivar todos los elementos primero
    this.menuItems.forEach(menuItem => {
      if (menuItem !== item) {
        menuItem.active = false;
      }
      
      // Activar padre si el item es hijo
      if (menuItem.children?.includes(item)) {
        menuItem.active = true;
      }
      
      // Procesar submenús
      if (menuItem.children) {
        menuItem.children.forEach(submenuItem => {
          if (submenuItem.children?.includes(item)) {
            menuItem.active = true;
            submenuItem.active = true;
          }
        });
      }
    });
    
  // console.timeEnd('setNavActive'); // SILENCIADO
  }

  // Optimización: Toggle más eficiente - CORREGIDO para mantener submenús abiertos
  toggletNavActive(item: Menu) {
    if (!item.active) {
      // Solo cerrar otros menús del mismo nivel (no hijos)
      this.menuItems.forEach(menuItem => {
        if (menuItem !== item && !this.isParentOf(menuItem, item)) {
          menuItem.active = false;
          
          // Solo cerrar submenús si no es padre del item actual
          if (menuItem.children && !this.isParentOf(menuItem, item)) {
            menuItem.children.forEach(child => {
              child.active = false;
            });
          }
        }
      });
    }
    
    item.active = !item.active;
  }

  // Método auxiliar para verificar si un item es padre de otro
  private isParentOf(parent: Menu, child: Menu): boolean {
    return parent.children ? parent.children.includes(child) : false;
  }

  // Optimización: navegación directa con preload y métricas
  navigateToSection(path: string) {
  // console.time('navigation-to-' + path); // SILENCIADO
    try {
      const ua = navigator.userAgent;
      const isSafari = /Safari\//.test(ua) && !/Chrome\//.test(ua) && !/Chromium\//.test(ua);
      const debugFlag = /[?&]debug=1/.test(location.search) || localStorage.getItem('DEBUG') === '1';
      if (!isSafari || debugFlag) {
        // eslint-disable-next-line no-console
  // console.log('🚀 [SIDEBAR] Navegando a:', path); // SILENCIADO
      }
    } catch {}
    
    // Marcar tiempo de inicio de navegación para métricas
    (window as any)[path.replace('/', '') + 'NavigationStart'] = performance.now();
    
    // Preload del módulo si es videoteca y mostrar overlay de carga en Safari
    if (path === '/Videoteca') {
  // console.log('📱 [SIDEBAR] Preparando navegación a Videoteca...'); // SILENCIADO
      (window as any).videotecaNavigationStart = performance.now();
      try {
        const ua = navigator.userAgent;
        const isSafari = /Safari\//.test(ua) && !/Chrome\//.test(ua) && !/Chromium\//.test(ua);
        if (isSafari) {
          // Evitar duplicar overlays
          const existing = document.getElementById('videoteca-safari-loading');
          if (!existing) {
            const overlay = document.createElement('div');
            overlay.id = 'videoteca-safari-loading';
            overlay.setAttribute('role', 'status');
            overlay.style.position = 'fixed';
            overlay.style.inset = '0';
            overlay.style.background = 'rgba(255,255,255,0.95)';
            overlay.style.zIndex = '99999';
            overlay.style.display = 'flex';
            overlay.style.alignItems = 'center';
            overlay.style.justifyContent = 'center';
            overlay.style.backdropFilter = 'none'; // evitar blur en Safari
            overlay.innerHTML = `
              <div style="text-align:center; background:#fff; border:1px solid #e9ecef; border-radius:12px; padding:24px; box-shadow:0 10px 30px rgba(0,0,0,0.1); max-width:380px; width:90%">
                <div style="display:flex; align-items:center; justify-content:center; gap:12px; margin-bottom:12px;">
                  <i class="fa fa-video-camera" style="font-size:28px; color:#0d6efd"></i>
                  <div class="spinner-border" style="width:1.5rem; height:1.5rem; color:#0d6efd" aria-hidden="true"></div>
                </div>
                <div style="font-weight:600; color:#333; margin-bottom:4px;">Cargando Videoteca…</div>
                <div style="color:#6c757d; font-size:14px;">Optimizado para Safari</div>
              </div>`;
            document.body.appendChild(overlay);
            // Auto-timeout de seguridad por si algo falla (se limpiará en Videoteca al cargar)
            setTimeout(() => {
              overlay.remove();
            }, 12000);
          }
        }
      } catch {}
    }
    
    // Navegación rápida
    // Usar navigateByUrl con rutas absolutas
  // Marcar estado navegando hasta completar
  this.isNavigating = true;
  this.cdr.markForCheck();
  this.router.navigateByUrl(path).then(() => {
      console.timeEnd('navigation-to-' + path);
      try {
        const ua = navigator.userAgent;
        const isSafari = /Safari\//.test(ua) && !/Chrome\//.test(ua) && !/Chromium\//.test(ua);
        const debugFlag = /[?&]debug=1/.test(location.search) || localStorage.getItem('DEBUG') === '1';
        if (!isSafari || debugFlag) {
          // eslint-disable-next-line no-console
          console.log('✅ [SIDEBAR] Navegación completada a:', path);
        }
      } catch {}
      this.isNavigating = false;
      this.cdr.markForCheck();
    }).catch(error => {
      console.error('❌ [SIDEBAR] Error en navegación:', error);
      console.timeEnd('navigation-to-' + path);
      this.isNavigating = false;
      this.cdr.markForCheck();
  // En caso de error, retirar overlay si quedó
  const overlay = document.getElementById('videoteca-safari-loading');
  if (overlay) overlay.remove();
    });
  }

  //Fileupload
  readUrl(event: any) {
    if (event.target.files.length === 0)
      return;
    //Image upload validation
    var mimeType = event.target.files[0].type;
    if (mimeType.match(/image\/*/) == null) {
      return;
    }
    // Image upload
    var reader = new FileReader();
    reader.readAsDataURL(event.target.files[0]);
    reader.onload = (_event) => {
      this.url = reader.result;
    }
  }

  goToAddCourse() {
    this.router.navigate(['/products/courses/add-course'], { queryParams: { mode: 'con' } });
  }

  goToAddCourseStep3() {
    this.router.navigate(['/products/courses/add-course'], { queryParams: { mode: 'sin' } });
  }

  // Validar que la URL de la imagen sea válida (no un número o valor inválido)
  isValidImageUrl(url: any): boolean {
    if (!url) return false;
    
    // Verificar que sea un string
    if (typeof url !== 'string') return false;
    
    // Verificar que no sea solo un número
    if (/^\d+(\.\d+)?$/.test(url)) return false;
    
    // Rechazar rutas del servidor (comienzan con / y contienen /var, /usr, /home, etc.)
    if (url.startsWith('/') && (url.includes('/var/') || url.includes('/usr/') || url.includes('/home/') || url.includes('/opt/'))) {
      return false;
    }
    
    // Verificar que comience con http, https, assets, o data:image (base64)
    if (url.startsWith('http') || url.startsWith('https') || url.startsWith('assets') || url.startsWith('data:image')) {
      return true;
    }
    
    return false;
  }

  // Manejar error de carga de imagen
  onImageError(event: any): void {
    // Reemplazar con imagen por defecto silenciosamente
    event.target.src = 'assets/images/dashboard/man.png';
  }

}
